import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  AlertTriangle, 
  User, 
  Clock, 
  MapPin, 
  Eye,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  FileText,
  Shield,
  Camera
} from 'lucide-react';
import { useSupervisorReports } from '@/hooks/useSupervisorReports';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SupervisorReportsListProps {
  teamId: string;
  onSelectReport?: (reportId: string) => void;
  dateFilter?: { start: Date; end: Date };
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical': return 'bg-red-100 text-red-800 border-red-200';
    case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'low': return 'bg-green-100 text-green-800 border-green-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
    case 'in-progress': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'closed': return 'bg-gray-100 text-gray-800 border-gray-200';
    default: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  }
};

const parseReportDescription = (description: any) => {
  if (typeof description === 'string') {
    try {
      return JSON.parse(description);
    } catch (e) {
      return { summary: description };
    }
  }
  return description || {};
};

const extractGuardEvaluations = (report: any) => {
  // First check if guard_evaluations field has data
  if (report.guard_evaluations && Object.keys(report.guard_evaluations).length > 0) {
    return report.guard_evaluations;
  }
  
  // If not, check if it's in the description JSON
  const parsedDescription = parseReportDescription(report.description);
  if (parsedDescription.guard_evaluations && Object.keys(parsedDescription.guard_evaluations).length > 0) {
    return parsedDescription.guard_evaluations;
  }
  
  return null;
};

const getReportTypeDisplay = (parsedDescription: any) => {
  if (parsedDescription.report_type === 'routine_inspection') {
    return 'Routine Guard Inspection';
  }
  if (parsedDescription.report_type) {
    return parsedDescription.report_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
  }
  return 'Supervisor Report';
};

export const SupervisorReportsList: React.FC<SupervisorReportsListProps> = ({ 
  teamId, 
  onSelectReport,
  dateFilter 
}) => {
  const { data: reports, isLoading, refetch } = useSupervisorReports(teamId);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedReports, setExpandedReports] = useState<Set<string>>(new Set());

  // Get guard names for all reports
  const { data: guardNamesMap } = useQuery({
    queryKey: ['guardNamesForReports', reports?.map(r => r.guard_id)],
    queryFn: async () => {
      if (!reports || reports.length === 0) return {};
      
      const guardIds = reports
        .map(r => r.guard_id)
        .filter((id): id is string => !!id);
      
      if (guardIds.length === 0) return {};
      
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .in('id', guardIds);
      
      const nameMap: Record<string, string> = {};
      data?.forEach((profile) => {
        nameMap[profile.id] = profile.full_name || 
          `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 
          'Unknown Guard';
      });
      
      return nameMap;
    },
    enabled: !!reports && reports.length > 0
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const toggleReportExpansion = (reportId: string) => {
    const newExpanded = new Set(expandedReports);
    if (newExpanded.has(reportId)) {
      newExpanded.delete(reportId);
    } else {
      newExpanded.add(reportId);
    }
    setExpandedReports(newExpanded);
  };

  // Auto-refresh every 30 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetch]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading supervisor reports...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          <h3 className="text-lg font-semibold">
            Supervisor Reports ({reports?.length || 0})
          </h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Reports List */}
      {!reports || reports.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No Reports Found</h3>
            <p className="text-muted-foreground">
              No supervisor reports found for this team.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => {
            const parsedDescription = parseReportDescription(report.description);
            const guardName = report.guard_id ? guardNamesMap?.[report.guard_id] : null;
            const guardEvaluations = extractGuardEvaluations(report);
            const isExpanded = expandedReports.has(report.id);
            
            return (
              <Card key={report.id} className="overflow-hidden">
                <Collapsible open={isExpanded} onOpenChange={() => toggleReportExpansion(report.id)}>
                  <CollapsibleTrigger asChild>
                    <div className="w-full cursor-pointer hover:bg-muted/50 transition-colors">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base font-medium truncate">
                                {report.title || getReportTypeDisplay(parsedDescription)}
                              </CardTitle>
                              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                <span>{format(new Date(report.created_at), 'dd/MM/yyyy HH:mm')}</span>
                                {guardName && <span>Guard: {guardName}</span>}
                                {report.location && <span>Location: {report.location}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                            <Badge className={`${getSeverityColor(report.severity)} text-xs`}>
                              {report.severity}
                            </Badge>
                            <Badge className={`${getStatusColor(report.status)} text-xs`}>
                              {report.status}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    {/* Supervisor */}
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Supervisor:</span>
                      <span className="font-medium truncate">
                        {report.supervisor_name || 'Unknown'}
                      </span>
                    </div>
                    
                    {/* Guard */}
                    {guardName && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-500" />
                        <span className="text-muted-foreground">Guard:</span>
                        <span className="font-medium truncate">{guardName}</span>
                      </div>
                    )}
                    
                    {/* Location */}
                    {report.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Location:</span>
                        <span className="font-medium truncate">{report.location}</span>
                      </div>
                    )}
                    
                    {/* Date */}
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Created:</span>
                      <span className="font-medium">
                        {format(new Date(report.created_at), 'dd/MM/yyyy HH:mm')}
                      </span>
                    </div>
                    
                    {/* Incident Time */}
                    {report.incident_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Incident:</span>
                        <span className="font-medium">
                          {format(new Date(report.incident_time), 'dd/MM/yyyy HH:mm')}
                        </span>
                      </div>
                    )}
                    
                    {/* Notes Count */}
                    {report.notes && Array.isArray(report.notes) && report.notes.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Notes:</span>
                        <Badge variant="outline" className="text-xs">
                          {report.notes.length}
                        </Badge>
                      </div>
                    )}
                  </div>
                  
                  {/* Guard Evaluations */}
                  {guardEvaluations && report.guard_id && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-medium mb-3 text-blue-600">Guard Evaluation</h4>
                      {Object.entries(guardEvaluations).map(([guardId, evaluation]: [string, any]) => (
                        <div key={guardId} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Performance Rating:</span>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                  key={star}
                                  className={`text-sm ${
                                    star <= parseInt(evaluation.performance_rating || '0')
                                      ? 'text-yellow-400'
                                      : 'text-gray-300'
                                  }`}
                                >
                                  ⭐
                                </span>
                              ))}
                              <span className="ml-2 text-sm font-medium">
                                {evaluation.performance_rating || 0}/5
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Compliance Status:</span>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                  key={star}
                                  className={`text-sm ${
                                    star <= parseInt(evaluation.compliance_status || '0')
                                      ? 'text-yellow-400'
                                      : 'text-gray-300'
                                  }`}
                                >
                                  ⭐
                                </span>
                              ))}
                              <span className="ml-2 text-sm font-medium">
                                {evaluation.compliance_status || 0}/5
                              </span>
                            </div>
                          </div>
                          
                          {evaluation.behavioral_observation && (
                            <div className="text-sm">
                              <span className="font-medium">Behavioral Observation:</span>
                              <p className="text-muted-foreground mt-1">{evaluation.behavioral_observation}</p>
                            </div>
                          )}
                          
                          {evaluation.safety_concerns && (
                            <div className="text-sm">
                              <span className="font-medium">Safety Concerns:</span>
                              <p className="text-muted-foreground mt-1">{evaluation.safety_concerns}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Photo Display */}
                  {report.image_url && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Camera className="h-4 w-4 text-blue-600" />
                        Photo Evidence
                      </h4>
                      <div className="relative">
                        <img 
                          src={report.image_url} 
                          alt="Report evidence" 
                          className="w-full max-w-md rounded-lg border shadow-sm"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Additional Report Details */}
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-600" />
                      Additional Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {parsedDescription.weather_conditions && (
                        <div>
                          <span className="font-medium">Weather:</span>
                          <span className="ml-2 text-muted-foreground">
                            {parsedDescription.weather_conditions.replace(/_/g, ' ')}
                          </span>
                        </div>
                      )}
                      {parsedDescription.security_equipment_status && (
                        <div>
                          <span className="font-medium">Equipment Status:</span>
                          <span className="ml-2 text-muted-foreground">
                            {parsedDescription.security_equipment_status}
                          </span>
                        </div>
                      )}
                      {parsedDescription.access_control_status && (
                        <div>
                          <span className="font-medium">Access Control:</span>
                          <span className="ml-2 text-muted-foreground">
                            {parsedDescription.access_control_status}
                          </span>
                        </div>
                      )}
                      {parsedDescription.surveillance_system_status && (
                        <div>
                          <span className="font-medium">Surveillance:</span>
                          <span className="ml-2 text-muted-foreground">
                            {parsedDescription.surveillance_system_status}
                          </span>
                        </div>
                      )}
                      {parsedDescription.lighting_status && (
                        <div>
                          <span className="font-medium">Lighting:</span>
                          <span className="ml-2 text-muted-foreground">
                            {parsedDescription.lighting_status}
                          </span>
                        </div>
                      )}
                      {parsedDescription.alarm_system_status && (
                        <div>
                          <span className="font-medium">Alarm System:</span>
                          <span className="ml-2 text-muted-foreground">
                            {parsedDescription.alarm_system_status}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {parsedDescription.security_inspection_notes && (
                      <div className="mt-3">
                        <span className="font-medium">Inspection Notes:</span>
                        <p className="text-muted-foreground mt-1">
                          {parsedDescription.security_inspection_notes}
                        </p>
                      </div>
                    )}
                  </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
